/* eslint react/jsx-props-no-spreading: 0 */
/* eslint react/prop-types: 0 */
/* eslint import/no-extraneous-dependencies: 0 */
import { useEffect, useMemo, useState } from 'react';
import { merge, omit } from 'lodash';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import { darken, lighten, alpha } from '@material-ui/core/styles/colorManipulator';

import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import ListIcon from '@material-ui/icons/List';
import TimelineIcon from '@material-ui/icons/Timeline';
import { Trans, t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { useId, useOptions } from '../../page-framework/panel/Panel';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { useQueryLoader } from '../../data-table/Loaders';
import { filterId as testFilterId } from '../../filters/test-filter/TestFilter';
import {
    getTestMetrics,
    getTestPathMetrics,
    getTestSearchFields,
    getTestPathSearchFields,
    TEST_TYPE_GROUPS,
} from './TestLogUtilities';
import useIsPrinting from '../../../utilities/PrintUtilities';
import { usePageStateValue } from '../../../stores/PageStateStore';
import { getDefaultColumns } from './columns/DefaultColumns';
import { getPathLogColumns } from './columns/PathLogColumns';
import { getWebColumns } from './columns/WebColumns';
import { getVoIPColumns } from './columns/VoIPColumns';
import { getCustomColumns } from './columns/CustomColumns';
import { useFilterState } from '../../../stores/FilterStateStore';
import { agentFilterId, siteFilterId, statusFilterId, timeRangeFilterId, useTest } from '../../filters';
import {
    PANEL_CATEGORIES,
    LOG_LIST_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { getStatusName } from '../../../utilities/StatusUtilities';
import { useDashboardContext } from '../../page-framework';
import { useFilterControl } from '../../page-framework/filter/FilterControl';
import { getPathAnalysisColumns } from './columns/PathAnalysisColumns';
import { PathChartTableViewportsProvider } from '../../path-visualization/ChartTableViewportsContext';
import { ROWSIZE } from '../../data-table/DataTableConstants';
import { useAllowDrillIn } from '../../../stores/SessionStore';

// Used to limit the number of records fetched by the data service for path analysis
export const PATH_ANALYSIS_API_LIMIT = 6;

const TEST_LOG_TAB = 0;
const PATH_LOG_TAB = 1;
const PATH_ANALYSIS_TAB = 2;
const DEFAULT_TAB = TEST_LOG_TAB;

const title = t`Service Test Log`;

const baseTestLogDataSource = {
    totalProperty: 'count',
    params: {
        type: 'agent,test',
    },
};

const useStyles = makeStyles((theme) => ({
    tabTable: {
        paddingTop: theme.spacing(1),
    },
    tab: {
        '& .MuiTab-wrapper': {
            flexDirection: 'row',

            '& svg': {
                marginRight: theme.spacing(1),
            },
        },
    },
    tabs: {
        borderBottom: `1px solid ${
            theme.palette.type === 'light'
                ? lighten(alpha(theme.palette.divider, 1), 0.85)
                : darken(alpha(theme.palette.divider, 1), 0.85)
        }`,

        '& .MuiTab-labelIcon': {
            minHeight: '50px',

            '& .MuiTab-wrapper > *:first-child': {
                marginBottom: 0,
            },
        },
    },
}));

function getDataSource(filterState, tabIndex) {
    const showPathLog = tabIndex === PATH_LOG_TAB;
    const test = filterState?.current?.[testFilterId];

    if (tabIndex === PATH_ANALYSIS_TAB) {
        return merge(
            {
                url: '/query/pathAnalysis',
                params: {
                    metrics: getTestPathMetrics(test),
                    searchFields: getTestPathSearchFields(test),
                },
            },
            baseTestLogDataSource
        );
    }

    return merge(
        {
            url: showPathLog ? '/query/pathLog' : '/query/testLog',
            params: {
                metrics: showPathLog ? getTestPathMetrics(test) : getTestMetrics(test),
                searchFields: showPathLog ? getTestPathSearchFields() : getTestSearchFields(test),
            },
        },
        baseTestLogDataSource
    );
}

function getTestColumns(test) {
    const testType = test?.typeNode;
    switch (testType?.group) {
        case TEST_TYPE_GROUPS.WEB:
            return getWebColumns(test);
        case TEST_TYPE_GROUPS.VOIP:
            return getVoIPColumns(test);
        default:
            return getCustomColumns(test);
    }
}

function getTestLogColumns(filterState) {
    const test = filterState?.current?.[testFilterId];
    return getTestColumns(test);
}

function getColumns(filterState, showPathLog, canDrillIn) {
    return [
        ...getDefaultColumns(filterState),
        ...(showPathLog ? getPathLogColumns(canDrillIn) : getTestLogColumns(filterState)),
    ];
}

const sorters = [{ property: 'testTime', direction: 'desc' }];

function TestLogPanel(props) {
    const id = useId();
    const filterControl = useFilterControl();
    const [filterState] = useFilterState();
    const isPrinting = useIsPrinting();
    const [canDrillIn] = useAllowDrillIn();

    const [isPathEnabled, setIsPathEnabled] = useState(false);
    const { editing } = useDashboardContext();
    const [options, setOptions] = useOptions();
    const tabIndexKey = `${id}-Tab`;
    const [tabIndex, pageStateActions] = usePageStateValue({
        key: tabIndexKey,
        defaultValue: options?.tabIndex || DEFAULT_TAB,
    });
    const [workingOptions, setWorkingOptions] = useState({
        tabIndex,
    });
    const [dataSource, columns] = useMemo(
        () => [getDataSource(filterState, tabIndex), getColumns(filterState, tabIndex === PATH_LOG_TAB, canDrillIn)],
        [filterState, tabIndex, canDrillIn]
    );
    const loader = useQueryLoader(dataSource);
    const classes = useStyles();
    const test = useTest();

    useEffect(() => {
        const hasPath = test?.traceRoute?.enabled || false;
        setIsPathEnabled(hasPath);

        if (test && !hasPath) handleTabChanged(DEFAULT_TAB);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [test]);

    useEffect(() => {
        // The path analysis API call is a bit expensive, so disable auto refresh for this tab
        if (tabIndex === PATH_ANALYSIS_TAB) {
            filterControl.pauseAutoRefresh();
        } else {
            filterControl.resumeAutoRefresh();
        }
    });

    function handleTabChanged(index) {
        if (index === null) return;

        const isDefaultTab = index === DEFAULT_TAB;
        const newOptions = isDefaultTab
            ? omit(options || {}, 'tabIndex')
            : {
                  ...(options || {}),
                  tabIndex: index,
              };

        if (editing) {
            setOptions(newOptions);
        } else {
            setWorkingOptions(newOptions);

            const clearPageState = index === options?.tabIndex || (isDefaultTab && !options?.tabIndex);

            if (clearPageState) {
                pageStateActions.clearValue(tabIndexKey);
            } else {
                pageStateActions.setValue(tabIndexKey, index);
            }
        }
    }

    if (!filterState) return null;

    const tab = (editing ? options?.tabIndex : workingOptions.tabIndex) || DEFAULT_TAB;

    return (
        <>
            {isPathEnabled && !isPrinting && (
                <Tabs
                    className={classes.tabs}
                    value={tab}
                    onChange={(e, index) => handleTabChanged(index)}
                    aria-label="test detail tabs"
                >
                    <Tab
                        id="test-log-tab-test-results"
                        className={classes.tab}
                        icon={<ListIcon />}
                        label={<Trans>Test Log</Trans>}
                    />
                    <Tab
                        id="test-log-tab-path-results"
                        className={classes.tab}
                        icon={<ListIcon />}
                        label={<Trans>Path Log</Trans>}
                    />
                    {canDrillIn && (
                        <Tab
                            id="test-log-tab-path-analysis"
                            className={classes.tab}
                            icon={<TimelineIcon />}
                            label={<Trans>Path Analysis</Trans>}
                        />
                    )}
                </Tabs>
            )}
            {tab === PATH_ANALYSIS_TAB ? (
                <PathChartTableViewportsProvider value={new Map()}>
                    <ReportingDataTable
                        tableId={`${id}-${test?.uuid}-PathAnalysis`}
                        className={clsx({ [classes.tabTable]: isPathEnabled })}
                        panelProps={props}
                        loader={loader}
                        columns={getPathAnalysisColumns()}
                        sorters={sorters}
                        limit={PATH_ANALYSIS_API_LIMIT}
                        showToolbar={false}
                        showHeaders={false}
                        rowSize={ROWSIZE.PATH_CHART}
                        pageStateKey={`${id}-PathAnalysis`}
                    />
                </PathChartTableViewportsProvider>
            ) : (
                <ReportingDataTable
                    tableId={`${id}-${test?.uuid}-Log`}
                    className={clsx({ [classes.tabTable]: isPathEnabled })}
                    panelProps={props}
                    loader={loader}
                    columns={columns}
                    sorters={sorters}
                />
            )}
        </>
    );
}

TestLogPanel.propTypes = panelPropTypes;

TestLogPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 200,
};

export default Panel({
    typeAlias: 'TestLog',
    catalogEntry: {
        category: PANEL_CATEGORIES.TEST,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`List of service test results`,
        previewImage: LOG_LIST_PANEL_PREVIEW_IMAGE,
    },
    filters: {
        required: [timeRangeFilterId, testFilterId],
        optional: [agentFilterId, siteFilterId, statusFilterId],
        // This is a temporary solution to deal with the fact that the test log uses different
        // values for the status filter than other pages do.  The proper solution is to fix the
        // APIs so that they all consistently expect the same values for the status filter.
        transformations: {
            [statusFilterId]: (state) => {
                return state.map((status) => getStatusName(status)?.toLowerCase());
            },
        },
    },
})(TestLogPanel);
